"use client";

import * as SliderPrimitive from "@radix-ui/react-slider";
import { useSession } from "@saas/auth/hooks/use-session";
import { UploadThumbnailActions } from "@saas/shared/assets/UploadThumbnailActions";
import { buildDisplayImageUrl } from "@saas/shared/lib/image-url-builder";
import { apiClient } from "@shared/lib/api-client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { cn } from "@ui/lib";
import {
	FolderIcon,
	ImagePlusIcon,
	Loader2Icon,
	MusicIcon,
	PauseIcon,
	PlayIcon,
	XIcon,
} from "lucide-react";
import { useSearchParams } from "next/navigation";
import { type DragEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { I2VReferenceMedia } from "../hooks/use-image-to-video";
import { getReferenceMediaKind } from "./ReferenceMediaThumb";

const AUDIO_ACCEPT =
	"audio/mpeg,audio/wav,audio/x-wav,audio/mp4,.mp3,.wav,.m4a";
const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";

export interface MusicVideoUploadLabels {
	songTitle: string;
	songHint: string;
	fromMyMusic: string;
	myMusicTitle: string;
	myMusicEmpty: string;
	clipHint: string;
	playClip: string;
	stopClip: string;
	changeSong: string;
	/** 历史 / 登录前现场带回来的那段：没有原曲，只能整段用 */
	clipReady: string;
	photoTitle: string;
	photoHint: string;
}

interface SongSource {
	blob: Blob;
	title: string;
	url: string;
	duration: number;
}

interface MyMusicTrack {
	id: string;
	title: string;
	audioUrl: string;
	imageUrl?: string;
	duration?: number;
}

function formatTime(seconds: number) {
	const s = Math.max(0, Math.round(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function readDuration(url: string): Promise<number> {
	return new Promise((resolve) => {
		const audio = new Audio();
		audio.preload = "metadata";
		audio.onloadedmetadata = () =>
			resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
		audio.onerror = () => resolve(0);
		audio.src = url;
	});
}

/**
 * 默认截哪段：整首里平均响度最大的那 seconds 秒，多半就是副歌（开头常是没人声的前奏）。
 * 解码失败就从头截
 */
async function loudestWindowStart(
	blob: Blob,
	seconds: number,
): Promise<number> {
	try {
		const context = new AudioContext();
		const buffer = await context
			.decodeAudioData(await blob.arrayBuffer())
			.finally(() => void context.close());
		const data = buffer.getChannelData(0);
		const perSecond = Math.floor(buffer.sampleRate);
		const energy: number[] = [];
		for (let i = 0; i + perSecond <= data.length; i += perSecond) {
			let sum = 0;
			for (let j = i; j < i + perSecond; j += 16) {
				sum += data[j] * data[j];
			}
			energy.push(sum);
		}
		const span = Math.min(seconds, energy.length);
		let best = 0;
		let bestSum = -1;
		let running = 0;
		for (let i = 0; i < energy.length; i++) {
			running += energy[i];
			if (i >= span) {
				running -= energy[i - span];
			}
			if (i >= span - 1 && running > bestSum) {
				bestSum = running;
				best = i - span + 1;
			}
		}
		return best;
	} catch {
		return 0;
	}
}

/**
 * 对口型 MV 页（/ai-music-video-generator）的上传区：上面是歌（上传或从 My Music 选，
 * 拖两个把手截 4–60 秒），下面是唱歌的那张照片。截好的一段交给 onClip 转 wav 上传，
 * 照片仍走工作台的 handleReferenceFiles
 */
export function MusicVideoUploads({
	audio,
	images,
	minSeconds,
	maxSeconds,
	defaultSeconds,
	labels,
	selectAssetLabel,
	processingLabel,
	onClip,
	onRemoveAudio,
	onPhotoFiles,
	onPickAsset,
	onPreviewImage,
	onEditImage,
	onRemoveImage,
}: {
	audio: I2VReferenceMedia | null;
	images: { previewUrl: string }[];
	minSeconds: number;
	maxSeconds: number;
	defaultSeconds: number;
	labels: MusicVideoUploadLabels;
	selectAssetLabel: string;
	processingLabel: (media: I2VReferenceMedia) => string;
	onClip: (blob: Blob, startSec: number, seconds: number) => void;
	onRemoveAudio: (id: string) => void;
	onPhotoFiles: (files: File[]) => void;
	onPickAsset: () => void;
	onPreviewImage: (index: number) => void;
	onEditImage: (index: number) => void;
	onRemoveImage: (index: number) => void;
}) {
	const { user } = useSession();
	const searchParams = useSearchParams();
	const songInputRef = useRef<HTMLInputElement>(null);
	const photoInputRef = useRef<HTMLInputElement>(null);
	const playerRef = useRef<HTMLAudioElement>(null);
	const [dragOver, setDragOver] = useState<"song" | "photo" | null>(null);
	const [source, setSource] = useState<SongSource | null>(null);
	const [range, setRange] = useState<[number, number]>([0, defaultSeconds]);
	const [loadingSong, setLoadingSong] = useState(false);
	const [pickerOpen, setPickerOpen] = useState(false);
	const [playing, setPlaying] = useState(false);
	// 最近一次修正过的截取范围：Radix 交给 onValueCommit 的是没修正的值，提交以这里为准
	const rangeRef = useRef<[number, number]>(range);
	// 上次交出去的范围：拖终点把起点带走、再拖回原值时 Radix 不触发 onValueCommit，
	// 松手时用它判断要不要补提交
	const lastSubmitted = useRef<[number, number] | null>(null);
	// 音频条目是不是本组件刚交出去的那段；Try this example、Reprompt 从外面换掉时清掉本地这首歌，
	// 不然界面和试听还是旧歌，提交的却是回填的那段
	const ownClipPending = useRef(false);

	useEffect(
		() => () => {
			if (source) {
				URL.revokeObjectURL(source.url);
			}
		},
		[source],
	);

	const audioId = audio?.id ?? null;
	const lastAudioId = useRef(audioId);
	useEffect(() => {
		if (audioId === lastAudioId.current) {
			return;
		}
		lastAudioId.current = audioId;
		if (ownClipPending.current) {
			ownClipPending.current = false;
			return;
		}
		setSource(null);
		setPlaying(false);
	}, [audioId]);

	const submitClip = (blob: Blob, start: number, seconds: number) => {
		ownClipPending.current = true;
		lastSubmitted.current = [start, start + seconds];
		onClip(blob, start, seconds);
	};

	const updateRange = (next: [number, number]) => {
		rangeRef.current = next;
		setRange(next);
	};

	const loadSong = async (blob: Blob, title: string) => {
		setLoadingSong(true);
		try {
			const url = URL.createObjectURL(blob);
			const duration = await readDuration(url);
			if (duration < minSeconds) {
				URL.revokeObjectURL(url);
				toast.error(
					duration
						? `The song must be at least ${minSeconds}s long.`
						: "We couldn't read this file. Please try an MP3, WAV or M4A.",
				);
				return;
			}
			const seconds = Math.min(defaultSeconds, duration);
			const start = Math.min(
				await loudestWindowStart(blob, seconds),
				Math.max(0, duration - seconds),
			);
			if (audio) {
				onRemoveAudio(audio.id);
			}
			setSource({ blob, title, url, duration });
			updateRange([start, start + seconds]);
			submitClip(blob, start, seconds);
		} finally {
			setLoadingSong(false);
		}
	};

	const loadTrack = async (track: { audioUrl: string; title: string }) => {
		setPickerOpen(false);
		setLoadingSong(true);
		try {
			// 歌已转存 images-art-prod，桶的 CORS 放行站内域名，浏览器直连。不走 /api/proxy-download：
			// 它整份读进内存再回，Vercel 函数响应上限 4.5MB，4 分钟的 Suno mp3 就超了。直连失败再退回代理
			let response = await fetch(track.audioUrl).catch(() => null);
			if (!response?.ok) {
				response = await fetch(
					`/api/proxy-download?url=${encodeURIComponent(track.audioUrl)}`,
				);
			}
			if (!response.ok) {
				throw new Error(`proxy ${response.status}`);
			}
			await loadSong(await response.blob(), track.title);
		} catch (error) {
			console.error("Failed to load the song:", error);
			toast.error("Couldn't load this song. Please try again.");
			setLoadingSong(false);
		}
	};

	// 音乐页结果卡「Lip sync video」带过来的 ?music=<生成 id>&track=<音轨 id>
	const musicId = searchParams.get("music");
	const trackId = searchParams.get("track");
	const deepLinkDone = useRef(false);
	// 只在拿到参数时跑一次（deepLinkDone 挡住重复）
	useEffect(() => {
		if (!user || !musicId || !trackId || deepLinkDone.current) {
			return;
		}
		deepLinkDone.current = true;
		void (async () => {
			const response = await apiClient.music.status[":id"]
				.$get({ param: { id: musicId } })
				.catch(() => null);
			const data = response?.ok
				? ((await response.json()) as {
						tracks?: MyMusicTrack[] | null;
					})
				: null;
			const track = data?.tracks?.find((item) => item.id === trackId);
			if (!track) {
				toast.error(
					"Couldn't find that song. Pick it from My Music instead.",
				);
				return;
			}
			await loadTrack(track);
		})();
	}, [user, musicId, trackId]);

	const myMusic = useQuery({
		queryKey: ["music-video-my-music"],
		enabled: pickerOpen && !!user,
		queryFn: async () => {
			const response = await apiClient.generations.history.$get({
				query: { page: "1", limit: "20", filter: "music" },
			});
			if (!response.ok) {
				throw new Error("Failed to fetch music");
			}
			const page = await response.json();
			return (
				page.data as unknown as {
					status: string;
					tracks: MyMusicTrack[] | null;
				}[]
			).flatMap((item) =>
				item.status === "SUCCESS" ? (item.tracks ?? []) : [],
			);
		},
	});

	// 键盘操作时 Radix 先调 onValueCommit 再调 onValueChange，推到 microtask 里，
	// 等同一轮的 changeRange 把 rangeRef 更新完再读
	const commitRange = () =>
		queueMicrotask(() => {
			if (!source) {
				return;
			}
			const [start, end] = rangeRef.current;
			const last = lastSubmitted.current;
			if (last && last[0] === start && last[1] === end) {
				return;
			}
			if (audio) {
				onRemoveAudio(audio.id);
			}
			submitClip(source.blob, start, end - start);
		});

	// 拖把手时把另一头带着走，长度夹在 [minSeconds, maxSeconds]
	const changeRange = ([start, end]: number[]) => {
		if (!source) {
			return;
		}
		const [prevStart] = range;
		let nextStart = start;
		let nextEnd = end;
		if (nextEnd - nextStart > maxSeconds) {
			if (nextStart !== prevStart) {
				nextEnd = nextStart + maxSeconds;
			} else {
				nextStart = nextEnd - maxSeconds;
			}
		}
		updateRange([nextStart, Math.min(nextEnd, source.duration)]);
	};

	const togglePlay = () => {
		const player = playerRef.current;
		if (!player) {
			return;
		}
		if (playing) {
			player.pause();
			setPlaying(false);
			return;
		}
		player.currentTime = range[0];
		void player.play();
		setPlaying(true);
	};

	const dropProps = (zone: "song" | "photo") => ({
		onDragOver: (event: DragEvent) => {
			event.preventDefault();
			setDragOver(zone);
		},
		onDragLeave: () => setDragOver(null),
		onDrop: (event: DragEvent) => {
			event.preventDefault();
			setDragOver(null);
			const files = Array.from(event.dataTransfer.files);
			if (zone === "song") {
				const file = files.find(
					(item) => getReferenceMediaKind(item) === "audio",
				);
				if (file) {
					void loadSong(file, file.name);
				}
				return;
			}
			onPhotoFiles(
				files.filter((item) => getReferenceMediaKind(item) === null),
			);
		},
	});

	const emptyZoneClass = (zone: "song" | "photo") =>
		cn(
			"flex w-full flex-col items-center justify-center rounded-2xl border border-dashed px-3 py-5 text-center transition-colors",
			dragOver === zone
				? "border-blue-400 bg-blue-50"
				: "border-zinc-300 bg-zinc-100 hover:border-zinc-400 hover:bg-zinc-200/60",
		);

	const clipSeconds = Math.round(range[1] - range[0]);

	return (
		<div className="flex flex-col gap-2">
			<input
				ref={songInputRef}
				type="file"
				accept={AUDIO_ACCEPT}
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0];
					event.target.value = "";
					if (file) {
						void loadSong(file, file.name);
					}
				}}
			/>
			<input
				ref={photoInputRef}
				type="file"
				accept={IMAGE_ACCEPT}
				className="hidden"
				onChange={(event) => {
					onPhotoFiles(Array.from(event.target.files ?? []));
					event.target.value = "";
				}}
			/>

			{source ? (
				<div className="rounded-2xl border border-zinc-200 bg-background p-3">
					{/* biome-ignore lint/a11y/useMediaCaption: 用户自己的歌，没有字幕轨 */}
					<audio
						ref={playerRef}
						src={source.url}
						preload="auto"
						className="hidden"
						onTimeUpdate={(event) => {
							if (event.currentTarget.currentTime >= range[1]) {
								event.currentTarget.pause();
								setPlaying(false);
							}
						}}
						onPause={() => setPlaying(false)}
					/>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={togglePlay}
							aria-label={
								playing ? labels.stopClip : labels.playClip
							}
							className="flex size-9 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white"
						>
							{playing ? (
								<PauseIcon className="size-4" />
							) : (
								<PlayIcon className="size-4" />
							)}
						</button>
						<span className="min-w-0 flex-1">
							<span className="block truncate font-medium text-sm text-zinc-800">
								{source.title}
							</span>
							<span className="block text-xs text-zinc-500 tabular-nums">
								{formatTime(range[0])}–{formatTime(range[1])} ·{" "}
								{clipSeconds}s
								{audio && (!audio.url || !audio.durationSec) ? (
									<Loader2Icon className="ml-1.5 inline size-3 animate-spin" />
								) : null}
							</span>
						</span>
						<button
							type="button"
							aria-label={labels.changeSong}
							onClick={() => {
								playerRef.current?.pause();
								setSource(null);
								if (audio) {
									onRemoveAudio(audio.id);
								}
							}}
							className="flex size-8 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100"
						>
							<XIcon className="size-4" />
						</button>
					</div>
					<SliderPrimitive.Root
						value={range}
						min={0}
						max={source.duration}
						step={1}
						minStepsBetweenThumbs={minSeconds}
						onValueChange={changeRange}
						onValueCommit={commitRange}
						onPointerUp={commitRange}
						className="relative mt-3 flex h-6 w-full touch-none select-none items-center"
					>
						<SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-zinc-200">
							<SliderPrimitive.Range className="absolute h-full bg-zinc-900" />
						</SliderPrimitive.Track>
						<SliderPrimitive.Thumb
							aria-label="Clip start"
							className="block size-5 rounded-full border-2 border-zinc-900 bg-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						/>
						<SliderPrimitive.Thumb
							aria-label="Clip end"
							className="block size-5 rounded-full border-2 border-zinc-900 bg-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						/>
					</SliderPrimitive.Root>
					<p className="mt-1.5 text-xs text-zinc-500">
						{labels.clipHint}
					</p>
				</div>
			) : audio ? (
				<div className="flex items-center gap-2 rounded-2xl border border-zinc-200 bg-background p-3">
					<MusicIcon className="size-5 shrink-0 text-zinc-600" />
					<span className="min-w-0 flex-1 text-sm text-zinc-700">
						{!audio.url || !audio.durationSec
							? processingLabel(audio)
							: `${labels.clipReady} · ${Math.round(audio.durationSec)}s`}
					</span>
					<button
						type="button"
						aria-label={labels.changeSong}
						onClick={() => onRemoveAudio(audio.id)}
						className="flex size-8 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100"
					>
						<XIcon className="size-4" />
					</button>
				</div>
			) : (
				<div
					data-missing-field="image"
					className={emptyZoneClass("song")}
					{...dropProps("song")}
				>
					<button
						type="button"
						disabled={loadingSong}
						onClick={() => songInputRef.current?.click()}
						className="flex flex-col items-center"
					>
						{loadingSong ? (
							<Loader2Icon className="mb-2 size-6 animate-spin text-zinc-700" />
						) : (
							<MusicIcon className="mb-2 size-6 text-zinc-700" />
						)}
						<span className="text-sm font-medium text-zinc-700">
							{labels.songTitle}
						</span>
						<span className="mt-0.5 text-xs text-zinc-500">
							{labels.songHint}
						</span>
					</button>
					{user ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={loadingSong}
							onClick={() => setPickerOpen(true)}
							className="mt-3 h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3.5 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
						>
							<MusicIcon className="h-3.5 w-3.5" />
							{labels.fromMyMusic}
						</Button>
					) : null}
				</div>
			)}

			{images.length ? (
				<div className="grid grid-cols-4 gap-2">
					{images.map((image, index) => (
						<div
							key={image.previewUrl}
							className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted/20"
						>
							<img
								src={buildDisplayImageUrl(
									image.previewUrl,
									384,
								)}
								alt={labels.photoTitle}
								className="h-full w-full object-cover"
							/>
							<UploadThumbnailActions
								onPreview={() => onPreviewImage(index)}
								onEdit={() => onEditImage(index)}
								onDelete={() => onRemoveImage(index)}
							/>
						</div>
					))}
				</div>
			) : (
				<div
					{...((source || audio) && {
						"data-missing-field": "image",
					})}
					className={emptyZoneClass("photo")}
					{...dropProps("photo")}
				>
					<button
						type="button"
						onClick={() => photoInputRef.current?.click()}
						className="flex flex-col items-center"
					>
						<ImagePlusIcon className="mb-2 size-6 text-zinc-700" />
						<span className="text-sm font-medium text-zinc-700">
							{labels.photoTitle}
						</span>
						<span className="mt-0.5 text-xs text-zinc-500">
							{labels.photoHint}
						</span>
					</button>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={onPickAsset}
						className="mt-3 h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3.5 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
					>
						<FolderIcon className="h-3.5 w-3.5" />
						{selectAssetLabel}
					</Button>
				</div>
			)}

			<Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>{labels.myMusicTitle}</DialogTitle>
					</DialogHeader>
					{myMusic.isLoading ? (
						<Loader2Icon className="mx-auto my-6 size-6 animate-spin text-muted-foreground" />
					) : myMusic.data?.length ? (
						<ul className="-mx-2 max-h-96 overflow-y-auto">
							{myMusic.data.map((track) => (
								<li key={track.id}>
									<button
										type="button"
										onClick={() => void loadTrack(track)}
										className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-zinc-100"
									>
										{track.imageUrl ? (
											<img
												src={track.imageUrl}
												alt=""
												className="size-10 shrink-0 rounded-md object-cover"
												loading="lazy"
											/>
										) : (
											<span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-zinc-100">
												<MusicIcon className="size-4 text-zinc-500" />
											</span>
										)}
										<span className="min-w-0 flex-1">
											<span className="block truncate font-medium text-sm">
												{track.title}
											</span>
											{track.duration ? (
												<span className="block text-xs text-zinc-500">
													{formatTime(track.duration)}
												</span>
											) : null}
										</span>
									</button>
								</li>
							))}
						</ul>
					) : (
						<p className="py-6 text-center text-sm text-muted-foreground">
							{labels.myMusicEmpty}
						</p>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
